import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  RiLink, 
  RiEdit2Line, 
  RiCheckLine, 
  RiCloseLine, 
  RiRefreshLine,
  RiHistoryLine,
  RiExternalLinkLine,
  RiSearchLine,
  RiCalendarEventLine,
  RiMapPinLine,
  RiTimeLine,
  RiAlertLine
} from 'react-icons/ri';
import { API_BASE } from '../config';
import axios from 'axios';
import toast from 'react-hot-toast';

const SlugManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [newSlug, setNewSlug] = useState('');
  const [slugHistory, setSlugHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/slug`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let eventsData = [];
      if (response.data && response.data.success && response.data.events) {
        eventsData = response.data.events;
      } else if (response.data && response.data.events) {
        eventsData = response.data.events;
      } else if (response.data && Array.isArray(response.data)) {
        eventsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        eventsData = response.data.data;
      } else {
        for (const key in response.data) {
          if (Array.isArray(response.data[key])) {
            eventsData = response.data[key];
            break;
          }
        }
      }
      
      setEvents(eventsData);
      if (eventsData.length === 0) {
        toast.success('No events found. Create your first event in Summit Management!');
      }
    } catch (error) {
      console.error('❌ Error fetching events:', error);
      setError(error.message);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found. Please check server configuration.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please check backend logs.');
      } else {
        toast.error('Failed to load events: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSlugHistory = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/slug/${eventId}/slug-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlugHistory(response.data.history || []);
      setSelectedEventId(eventId);
      setShowHistory(true);
    } catch (error) {
      console.error('Error fetching slug history:', error);
      toast.error('Failed to load slug history');
    }
  };

  const handleEditSlug = (event) => {
    setEditingEvent(event);
    setNewSlug(event.slug || '');
  };

  const handleUpdateSlug = async () => {
    if (!newSlug.trim()) {
      toast.error('Slug cannot be empty');
      return;
    }

    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(newSlug)) {
      toast.error('Slug must contain only lowercase letters, numbers, and hyphens');
      return;
    }

    setIsUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE}/api/slug/${editingEvent._id}/slug`,
        { customSlug: newSlug },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Slug updated successfully to: ${newSlug}`);
        setEditingEvent(null);
        setNewSlug('');
        fetchEvents();
      } else {
        toast.error(response.data.message || 'Failed to update slug');
      }
    } catch (error) {
      console.error('Error updating slug:', error);
      toast.error(error.response?.data?.message || 'Failed to update slug');
    } finally {
      setIsUpdating(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
  };

  const filteredEvents = events.filter(event =>
    event.title?.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    event.slug?.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <RiLink className="text-blue-600" />
            Slug Management
          </h2>
          <p className="text-gray-600 mt-1">
            Manage URL slugs for all events and conferences
          </p>
        </div>
        <button
          onClick={fetchEvents}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
        >
          <RiRefreshLine className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search events by title or slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">Error: {error}</p>
          <button
            onClick={fetchEvents}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Retry
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
            <p className="mt-2 text-gray-600">Loading events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2">📅</div>
            <p className="text-gray-600">No events found</p>
            <p className="text-sm text-gray-400 mt-2">
              Create events in the Summit Management page first.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredEvents.map((event) => (
              <div key={event._id} className="p-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-900">
                        {event.title}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        event.status === 'upcoming' ? 'bg-green-100 text-green-700' :
                        event.status === 'ongoing' ? 'bg-blue-100 text-blue-700' :
                        event.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {event.status}
                      </span>
                    </div>
                    
                    <div className="mt-2 flex items-center gap-4 text-sm text-gray-900">
                      <div className="flex items-center gap-1 text-gray-700">
                        <RiLink size={14} className="text-gray-500" />
                        <span>URL: <code className="bg-gray-100 px-2 py-0.5 rounded text-blue-600">/{event.slug}</code></span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-700">
                        <RiCalendarEventLine size={14} className="text-gray-500" />
                        <span>{event.date ? new Date(event.date).toLocaleDateString() : 'TBD'}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 text-gray-700">
                          <RiMapPinLine size={14} className="text-gray-500" />
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => fetchSlugHistory(event._id)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="View slug history"
                    >
                      <RiHistoryLine size={18} />
                    </button>
                    <button
                      onClick={() => handleEditSlug(event)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Edit slug"
                    >
                      <RiEdit2Line size={18} />
                    </button>
                    <a
                      href={`/${event.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                      title="View event"
                    >
                      <RiExternalLinkLine size={18} />
                    </a>
                  </div>
                </div>

                {editingEvent?._id === event._id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-900 block mb-1">
                          Custom Slug
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-700 text-sm">/</span>
                          <input
                            type="text"
                            value={newSlug}
                            onChange={(e) => setNewSlug(e.target.value)}
                            placeholder="custom-slug"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                          />
                          <button
                            onClick={() => setNewSlug(generateSlug(event.title))}
                            className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition text-gray-700"
                          >
                            Auto-generate
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-6">
                        <button
                          onClick={handleUpdateSlug}
                          disabled={isUpdating}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                        >
                          {isUpdating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                              Updating...
                            </>
                          ) : (
                            <>
                              <RiCheckLine />
                              Update Slug
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingEvent(null);
                            setNewSlug('');
                          }}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
                <RiHistoryLine className="text-blue-600" />
                Slug History
              </h3>
              <button
                onClick={() => {
                  setShowHistory(false);
                  setSelectedEventId(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700"
              >
                <RiCloseLine size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {slugHistory.length === 0 ? (
                <p className="text-gray-500 text-center">No slug history available</p>
              ) : (
                <div className="space-y-3">
                  {slugHistory.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <code className="text-sm text-gray-900">{item.oldSlug}</code>
                        <p className="text-xs text-gray-500 mt-1">
                          Changed on {new Date(item.changedAt).toLocaleString()}
                        </p>
                      </div>
                      {index === 0 && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                          Latest
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SlugManagement;